// src/modules/app-views/welcome/pages/Init/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import useApi from 'hooks/useApi';
import useZustand from 'hooks/useZustand';
import WelcomeCard from './WelcomeCard';
import NextSteps from './NextSteps';
import QuickAccessModules from './QuickAccessModules';
import ApplicationTour from 'layouts/Tour/ApplicationTour';
import { useState, useEffect, useMemo } from 'react';
import { useTourStore } from 'stores/tour';
import appService from 'services/appService';
import FirstSubscriptionModal from './modals/FirstSubscriptionModal';
import PendingInvitationsModal from './modals/PendingInvitationsModal';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// *******************************************************************************
// STYLED COMPONENTS
// *******************************************************************************
const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const WelcomeInit = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedUser = useZustand('auth', 'user');
  const storedToken = useZustand('auth', 'token');
  const storedParameters = useZustand('app', 'parameters');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // useApi({ hideLoading: true });
  const openTour = useTourStore(s => s.openTour);
  // NOTE: hasCreatedDefaultPreferenceRef was removed — see effect below for rationale

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Check if should show first subscription modal for new users
  // IMPORTANT: Also check for valid token to prevent modal from showing during logout
  // IMPORTANT: Also check for valid user to prevent modal from showing before auth is complete
  const shouldShowFirstSubscriptionModal = useMemo(() => {
    // Don't show modal if user is being logged out (no token)
    if (!storedToken) return false;

    // Don't show modal if user hasn't been loaded yet (prevents race condition on page load/refresh)
    if (!storedUser?._id) return false;

    const hasNoSubscriptions = !storedUser?.subscriptions?.length;
    const neverBeenOwner = !storedUser?.hasBeenSubscriptionOwner;
    return hasNoSubscriptions && neverBeenOwner;
  }, [storedToken, storedUser?._id, storedUser?.subscriptions, storedUser?.hasBeenSubscriptionOwner]);

  // Check if user has pending invitations (status 1 = invited)
  const pendingInvitations = useMemo(() => {
    if (!storedUser?.subscriptions?.length) return [];
    return storedUser.subscriptions.filter(s => s.status === 1);
  }, [storedUser?.subscriptions]);

  const shouldShowPendingInvitationsModal = useMemo(() => {
    if (!storedToken || !storedUser?._id) return false;
    return pendingInvitations.length > 0;
  }, [storedToken, storedUser?._id, pendingInvitations]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showNextStepsSession, setShowNextStepsSession] = useState(false);
  const [showQuickAccessModules, setShowQuickAccessModules] = useState(true);
  const [showFirstSubscriptionModal, setShowFirstSubscriptionModal] = useState(false);
  const [showPendingInvitationsModal, setShowPendingInvitationsModal] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────

  // Sync local state with store preferences.
  // NOTE: the backend strips falsy values, so `showNextStepsSession: false` is never
  // persisted. We use `done: true` (truthy, always persisted) as the "permanently hidden"
  // anchor. `showNextStepsSession: true` (also truthy) acts as an explicit "re-show"
  // override set by the WelcomeCard toggle — it beats `done` and brings the panel back.
  useEffect(() => {
    if (!storedUser?._id || !storedParameters) return;

    const isDone = appService.getModulePreferences({
      category: 'tour',
      moduleId: 'welcome',
      key: 'done',
    });

    const showOverride = appService.getModulePreferences({
      category: 'tour',
      moduleId: 'welcome',
      key: 'showNextStepsSession',
    });

    const result = !isDone || showOverride === true;
    setShowNextStepsSession(result);
  }, [storedUser?._id, storedParameters]);

  // Sync quick access modules visibility with store preferences
  useEffect(() => {
    if (!storedUser?._id || !storedParameters) return;

    const shouldShow = appService.getModulePreferences({
      category: 'widgets',
      moduleId: 'welcome',
      key: 'showQuickAccessModules',
    });
    // Default to true if not set
    setShowQuickAccessModules(shouldShow ?? true);
  }, [storedUser?._id, storedParameters]);

  // Check if should show pending invitations modal (takes priority over first subscription modal)
  // Only show once per login session — token changes on each login, so sessionStorage key
  // becomes stale on logout+login, ensuring the modal appears again on fresh login.
  useEffect(() => {
    if (!shouldShowPendingInvitationsModal) return;

    const SESSION_KEY = 'pendingInvitationsModalShownToken';
    const alreadyShownThisSession = sessionStorage.getItem(SESSION_KEY) === storedToken;

    if (!alreadyShownThisSession) {
      sessionStorage.setItem(SESSION_KEY, storedToken);
      setShowPendingInvitationsModal(true);
    }
  }, [shouldShowPendingInvitationsModal, storedToken]);

  // Check if should show first subscription modal (only if no pending invitations)
  useEffect(() => {
    if (shouldShowFirstSubscriptionModal && !shouldShowPendingInvitationsModal) {
      setShowFirstSubscriptionModal(true);
    }
  }, [shouldShowFirstSubscriptionModal, shouldShowPendingInvitationsModal]);
  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleStartTour = () => {
    openTour(ApplicationTour);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Container>
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        {/* Welcome Card */}
        <WelcomeCard
        // showNextStepsSession={showNextStepsSession}
        // setShowNextStepsSession={setShowNextStepsSession}
        />

        {/* Next Steps - Conditionally rendered based on user preferences */}
        <AnimatePresence mode="wait">
          {showNextStepsSession && (
            <NextSteps
              key="next-steps"
              onStartTour={handleStartTour}
              // showSession={showNextStepsSession}
              // setShowSession={setShowNextStepsSession}
            />
          )}
        </AnimatePresence>

        {/* Quick Access Modules - Conditionally rendered based on user preferences */}
        <AnimatePresence mode="wait">
          {showQuickAccessModules && (
            <QuickAccessModules
              key="quick-access-modules"
              onClose={() => setShowQuickAccessModules(false)}
            />
          )}
        </AnimatePresence>
      </motion.div>

      {/* Pending Invitations Modal - for users with pending invitations (priority over first subscription) */}
      {showPendingInvitationsModal && (
        <PendingInvitationsModal
          show={showPendingInvitationsModal}
          setShow={setShowPendingInvitationsModal}
          invitations={pendingInvitations}
        />
      )}

      {/* First Subscription Modal - for new users who have never been subscription owner */}
      {showFirstSubscriptionModal && !showPendingInvitationsModal && (
        <FirstSubscriptionModal
          show={showFirstSubscriptionModal}
          setShow={setShowFirstSubscriptionModal}
        />
      )}
    </Container>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default WelcomeInit;

// *******************************************************************************
// HELPERS
// *******************************************************************************
